import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { reportService } from '@/services/reportService';
import * as ReportTypes from '@/types/report.types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, Download, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { ScrollArea } from '@/components/ui/scroll-area';

interface VonCompletenessReportProps {
    hospitalId?: number;
    startDate?: string;
    endDate?: string;
}

export const VonCompletenessReport: React.FC<VonCompletenessReportProps> = ({
    hospitalId,
    startDate,
    endDate
}) => {
    const [data, setData] = useState<ReportTypes.VonCompletenessReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await reportService.getVonCompletenessReport({
                    hospital_id: hospitalId,
                    start_date: startDate,
                    end_date: endDate
                });
                setData(result);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Eroare la încărcarea datelor');
                console.error('Error fetching VON completeness report:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [hospitalId, startDate, endDate]);

    const handleExportPdf = async () => {
        if (!data) return;
        
        try {
            const blob = await reportService.exportToPdf({
                report_data: data,
                report_type: 'von_completeness',
                title: 'Raport completitudine date RONN'
            });
            
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `von_completitudine_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast({
                title: "Export reușit",
                description: "Raportul a fost exportat ca PDF"
            });
        } catch (err) {
            toast({
                title: "Eroare la export",
                description: "Nu s-a putut exporta raportul ca PDF",
                variant: "destructive"
            });
        }
    };

    const handleExportExcel = async () => {
        if (!data) return;
        
        try {
            const blob = await reportService.exportToExcel({
                report_data: data,
                report_type: 'von_completeness',
                title: 'Raport completitudine date RONN'
            });
            
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `von_completitudine_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            toast({
                title: "Export reușit",
                description: "Raportul a fost exportat ca Excel"
            });
        } catch (err) {
            toast({
                title: "Eroare la export",
                description: "Nu s-a putut exporta raportul ca Excel",
                variant: "destructive"
            });
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-32 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <Info className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    if (!data) {
        return (
            <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>Nu există date de afișat</AlertDescription>
            </Alert>
        );
    }

    const getStatusColor = (percentage: number) => {
        if (percentage >= 80) return "bg-green-500";
        if (percentage >= 50) return "bg-yellow-500";
        return "bg-red-500";
    };

    const getStatusIcon = (percentage: number) => {
        if (percentage >= 80) return <CheckCircle className="h-5 w-5 text-green-500" />;
        if (percentage >= 50) return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
        return <XCircle className="h-5 w-5 text-red-500" />;
    };

    return (
        <div className="space-y-6">
            {/* Header with export buttons */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold">Raport completitudine date RONN</h2>
                    <p className="text-muted-foreground">
                        Total pacienți eligibili: <span className="font-semibold">{data.total_patients}</span>
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportPdf}>
                        <Download className="h-4 w-4 mr-2" />
                        Export PDF
                    </Button>
                    <Button variant="outline" onClick={handleExportExcel}>
                        <Download className="h-4 w-4 mr-2" />
                        Export Excel
                    </Button>
                </div>
            </div>

            {/* Overall Status */}
            <Card>
                <CardHeader>
                    <CardTitle>Status general</CardTitle>
                    <CardDescription>
                        Progresul general al completării datelor pentru perioada selectată
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm font-medium">
                                <span>Completitudine generală</span>
                                <span>{data.overall_completeness}%</span>
                            </div>
                            <Progress value={data.overall_completeness} className="h-4" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="flex items-center space-x-4 p-4 border rounded-lg">
                                <div className="p-2 bg-green-100 rounded-full">
                                    <CheckCircle className="h-6 w-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground">Secțiuni Complete</p>
                                    <p className="text-2xl font-bold">{data.summary.complete_sections}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4 p-4 border rounded-lg">
                                <div className="p-2 bg-yellow-100 rounded-full">
                                    <AlertTriangle className="h-6 w-6 text-yellow-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground">Necesită Atenție</p>
                                    <p className="text-2xl font-bold">{data.summary.sections_needing_attention}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4 p-4 border rounded-lg">
                                <div className="p-2 bg-red-100 rounded-full">
                                    <XCircle className="h-6 w-6 text-red-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground">Secțiuni Critice</p>
                                    <p className="text-2xl font-bold">{data.summary.critical_sections}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Detailed Sections */}
            <Tabs defaultValue="sections" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="sections">Detalii pe Secțiuni</TabsTrigger>
                    <TabsTrigger value="missing">Pacienți cu Date Lipsă</TabsTrigger>
                </TabsList>

                <TabsContent value="sections" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {data.sections.map((section, index) => (
                            <Card key={index}>
                                <CardHeader className="pb-2">
                                    <div className="flex justify-between items-start">
                                        <CardTitle className="text-lg">{section.name}</CardTitle>
                                        {getStatusIcon(section.completeness_percentage)}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-muted-foreground">Progres</span>
                                                <span className="font-medium">{section.completeness_percentage}%</span>
                                            </div>
                                            <Progress value={section.completeness_percentage} className="h-2" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div>
                                                <p className="text-muted-foreground">Pacienți cu secțiune</p>
                                                <p className="font-medium">{section.patients_with_section}</p>
                                            </div>
                                            <div>
                                                <p className="text-muted-foreground">Completate integral</p>
                                                <p className="font-medium">{section.patients_with_complete_section}</p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="missing" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Pacienți cu Date Lipsă</CardTitle>
                            <CardDescription>
                                Lista pacienților care au secțiuni lipsă sau incomplete
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Cod pacient</TableHead>
                                        <TableHead>Nume</TableHead>
                                        <TableHead>Data nașterii</TableHead>
                                        <TableHead>Vârstă gest.</TableHead>
                                        <TableHead>Greutate</TableHead>
                                        <TableHead>Secțiuni lipsă</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.patients_with_missing_data.map((patient) => (
                                        <TableRow key={patient.id}>
                                            <TableCell className="font-medium">{patient.patient_code}</TableCell>
                                            <TableCell>{patient.name}</TableCell>
                                            <TableCell>{new Date(patient.birth_date).toLocaleDateString('ro-RO')}</TableCell>
                                            <TableCell>{patient.gestational_age} săpt.</TableCell>
                                            <TableCell>{patient.birth_weight}g</TableCell>
                                            <TableCell>
                                                <Badge variant="destructive">{patient.missing_sections}</Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};
